import React, { useState, useEffect, useMemo } from 'react';
import { 
  Clock, MessageSquare, Mail, Brain, Globe, Bell, AlertTriangle, 
  Plus, Trash2, Save, ChevronDown, ChevronRight, Power,
  Zap, Users, Settings, Edit2, Copy, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import getApiUrl from '../../utils/getApiUrl';

const API = `${getApiUrl()}/api`;
const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, { ...options, credentials: "include", headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
};

// ═══════════════════ SECTION HEADER ═══════════════════
const SectionHeader = ({ icon: Icon, title, subtitle, color, count, expanded, onToggle }) => (
  <button onClick={onToggle} className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors">
    <div className="flex items-center gap-3">
      <Icon size={20} className={color} />
      <div className="text-left">
        <h3 className="text-base font-bold text-white">{title}</h3>
        <p className="text-xs text-slate-400">{subtitle}</p>
      </div>
    </div>
    <div className="flex items-center gap-2">
      <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-700 text-slate-300">{count}</span>
      {expanded ? <ChevronDown size={18} className="text-slate-400" /> : <ChevronRight size={18} className="text-slate-400" />}
    </div>
  </button>
);

// ═══════════════════ GENERIC ITEM EDITOR ═══════════════════
const ItemEditor = ({ item, fields, onChange, onDelete }) => {
  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 grid grid-cols-2 gap-3">
          {fields.map(f => (
            <div key={f.key} className={f.wide ? 'col-span-2' : ''}>
              <label className="text-xs text-slate-400 mb-1 block">{f.label}</label>
              {f.type === 'textarea' ? (
                <textarea
                  value={item[f.key] || ''}
                  onChange={e => onChange(f.key, e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-sm resize-none"
                  placeholder={f.placeholder || ''}
                />
              ) : f.type === 'select' ? (
                <select
                  value={item[f.key] || ''}
                  onChange={e => onChange(f.key, e.target.value)}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-sm"
                >
                  <option value="">—</option>
                  {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              ) : f.type === 'toggle' ? (
                <button
                  onClick={() => onChange(f.key, !item[f.key])}
                  className={`px-3 py-2 rounded-lg text-sm font-medium ${item[f.key] ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-700 text-slate-500'}`}
                >
                  {item[f.key] ? 'Actif' : 'Inactif'}
                </button>
              ) : (
                <input
                  type={f.type || 'text'}
                  value={item[f.key] || ''}
                  onChange={e => onChange(f.key, e.target.value)}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-sm"
                  placeholder={f.placeholder || ''}
                />
              )}
            </div>
          ))}
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <button
            onClick={() => onChange('actif', !item.actif)}
            className={`p-2 rounded-lg ${item.actif ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-700 text-slate-500'}`}
            title={item.actif ? 'Actif' : 'Inactif'}
          >
            <Power size={14} />
          </button>
          <button
            onClick={onDelete}
            className="p-2 hover:bg-red-500/20 rounded-lg text-red-400"
            title="Supprimer"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════ GENERIC SECTION ═══════════════════
const AutomationSection = ({ type, icon, title, subtitle, color, borderColor, fields, defaultItem }) => {
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (expanded && !loaded) {
      authFetch(`${API}/config/automation/${type}`).then(r => r.json()).then(d => {
        setItems(d.data || []);
        setLoaded(true);
      }).catch(() => {});
    }
  }, [expanded, loaded, type]);

  const save = async () => {
    setSaving(true);
    try {
      const resp = await authFetch(`${API}/config/automation/${type}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(items)
      });
      if (resp.ok) toast.success(`${title} sauvegardées`);
      else toast.error('Erreur sauvegarde');
    } catch { toast.error('Erreur réseau'); }
    setSaving(false);
  };

  const addItem = () => {
    setItems([...items, { ...defaultItem, id: `${type}_${Date.now()}` }]);
  };

  const updateItem = (index, key, value) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [key]: value };
    setItems(updated);
  };

  const deleteItem = (index) => {
    if (confirm(`Supprimer "${items[index]?.nom || 'cet élément'}" ?`)) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const activeCount = items.filter(i => i.actif).length;

  return (
    <div data-testid={`automation-${type}`} className={`bg-gradient-to-r ${color} border ${borderColor} rounded-xl overflow-hidden`}>
      <SectionHeader
        icon={icon}
        title={title}
        subtitle={`${activeCount}/${items.length} actif${activeCount > 1 ? 's' : ''}`}
        color={`text-${borderColor.replace('border-', '').replace('/30', '')}`}
        count={items.length}
        expanded={expanded}
        onToggle={() => setExpanded(!expanded)}
      />
      {expanded && (
        <div className="px-4 pb-4 space-y-3">
          <div className="flex justify-end gap-2">
            <button onClick={addItem} className="px-3 py-1.5 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 rounded-lg text-xs flex items-center gap-1.5">
              <Plus size={14} /> Ajouter
            </button>
            <button onClick={save} disabled={saving} className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-xs flex items-center gap-1.5">
              <Save size={14} /> {saving ? 'Enregistrement...' : 'Sauvegarder'}
            </button>
          </div>
          {items.length === 0 ? (
            <p className="text-xs text-slate-500 text-center py-6">Aucun élément configuré</p>
          ) : items.map((item, i) => (
            <ItemEditor
              key={item.id || i}
              item={item}
              fields={fields}
              onChange={(key, val) => updateItem(i, key, val)}
              onDelete={() => deleteItem(i)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ═══════════════════ MAIN COMPONENT ═══════════════════
const AutomationPanel = () => {
  return (
    <div className="space-y-3 mt-4">
      <div className="flex items-center gap-2 mb-2">
        <Settings size={18} className="text-slate-400" />
        <h2 className="text-lg font-bold text-white">Moteur d'Automatisation</h2>
        <span className="text-xs text-slate-500 ml-2">Événement → Condition → Action</span>
      </div>

      {/* WEBHOOKS / ÉVÉNEMENTS */}
      <AutomationSection
        type="webhooks_events"
        icon={Zap}
        title="Événements & Déclencheurs"
        subtitle="Réagir aux changements dans le pipeline"
        color="from-amber-600/10 to-orange-600/10"
        borderColor="border-amber-500/30"
        defaultItem={{ nom: '', evenement: '', conditions: [], actions: [], actif: true }}
        fields={[
          { key: 'nom', label: 'Nom', placeholder: 'Ex: Quand R1 pris → lancer confirmation' },
          { key: 'evenement', label: 'Événement déclencheur', type: 'select', options: [
            { value: 'changement_etape', label: 'Changement d\'étape' },
            { value: 'creation_lead', label: 'Création de lead' },
            { value: 'rdv_pris', label: 'RDV R1 pris' },
            { value: 'rdv_annule', label: 'RDV annulé' },
            { value: 'nrp', label: 'NRP enregistré' },
            { value: 'reception_sms', label: 'Réception SMS client' },
            { value: 'reception_email', label: 'Réception email' },
          ]},
          { key: 'description', label: 'Description / Condition', wide: true, type: 'textarea', placeholder: 'Décrivez quand cet événement se déclenche et quelles actions il lance...' },
        ]}
      />

      {/* CRONS / TÂCHES PLANIFIÉES */}
      <AutomationSection
        type="crons"
        icon={Clock}
        title="Tâches Planifiées (Crons)"
        subtitle="Actions récurrentes automatiques"
        color="from-blue-600/10 to-indigo-600/10"
        borderColor="border-blue-500/30"
        defaultItem={{ nom: '', frequence: 'daily_9h', condition: '', action_type: '', actif: true }}
        fields={[
          { key: 'nom', label: 'Nom', placeholder: 'Ex: Confirmation RDV J-2' },
          { key: 'frequence', label: 'Fréquence', type: 'select', options: [
            { value: 'daily_9h', label: 'Tous les jours à 9h' },
            { value: 'daily_14h', label: 'Tous les jours à 14h' },
            { value: 'every_15min', label: 'Toutes les 15 min' },
            { value: 'every_1h', label: 'Toutes les heures' },
            { value: 'weekly_monday', label: 'Chaque lundi' },
            { value: 'custom', label: 'Expression cron personnalisée' },
          ]},
          { key: 'condition', label: 'Condition', wide: true, type: 'textarea', placeholder: 'Ex: Clients avec date_r1 dans 2 jours ET pas de confirmation reçue' },
          { key: 'action_type', label: 'Action à déclencher', type: 'select', options: [
            { value: 'send_sms', label: 'Envoyer SMS' },
            { value: 'send_email', label: 'Envoyer Email' },
            { value: 'change_etape', label: 'Changer d\'étape' },
            { value: 'notify', label: 'Notifier l\'équipe' },
            { value: 'ai_action', label: 'Action IA' },
          ]},
        ]}
      />

      {/* TEMPLATES SMS */}
      <AutomationSection
        type="templates_sms"
        icon={MessageSquare}
        title="Templates SMS"
        subtitle="Modèles de messages avec variables dynamiques"
        color="from-emerald-600/10 to-teal-600/10"
        borderColor="border-emerald-500/30"
        defaultItem={{ nom: '', contenu: '', variables: [], categorie: '', actif: true }}
        fields={[
          { key: 'nom', label: 'Nom du template', placeholder: 'Ex: Confirmation RDV J-2' },
          { key: 'categorie', label: 'Catégorie', type: 'select', options: [
            { value: 'confirmation', label: 'Confirmation' },
            { value: 'relance', label: 'Relance' },
            { value: 'info', label: 'Information' },
            { value: 'urgence', label: 'Urgence' },
          ]},
          { key: 'contenu', label: 'Contenu du SMS', wide: true, type: 'textarea', placeholder: 'Bonjour {nom}, votre RDV est prévu le {date_r1} à {heure_r1}. Merci de confirmer en répondant OUI.' },
        ]}
      />

      {/* TEMPLATES EMAIL */}
      <AutomationSection
        type="templates_email"
        icon={Mail}
        title="Templates Email"
        subtitle="Modèles d'emails avec variables dynamiques"
        color="from-violet-600/10 to-purple-600/10"
        borderColor="border-violet-500/30"
        defaultItem={{ nom: '', objet: '', corps: '', variables: [], categorie: '', actif: true }}
        fields={[
          { key: 'nom', label: 'Nom du template', placeholder: 'Ex: Email bienvenue nouveau lead' },
          { key: 'objet', label: 'Objet de l\'email', placeholder: 'Ex: Confirmation de votre rendez-vous - {date_r1}' },
          { key: 'categorie', label: 'Catégorie', type: 'select', options: [
            { value: 'confirmation', label: 'Confirmation' },
            { value: 'bienvenue', label: 'Bienvenue' },
            { value: 'relance', label: 'Relance' },
            { value: 'info', label: 'Information' },
          ]},
          { key: 'corps', label: 'Corps de l\'email', wide: true, type: 'textarea', placeholder: 'Bonjour {nom},\n\nNous confirmons votre rendez-vous le {date_r1} à {heure_r1}.\n\nCordialement,\nL\'équipe Sani-Adapt' },
        ]}
      />

      {/* ACTIONS IA */}
      <AutomationSection
        type="actions_ia"
        icon={Brain}
        title="Actions IA"
        subtitle="Automatismes intelligents (Christelle, Yaël, Rachel)"
        color="from-pink-600/10 to-rose-600/10"
        borderColor="border-pink-500/30"
        defaultItem={{ nom: '', agent: '', declencheur: '', prompt_template: '', action_sortie: '', actif: true }}
        fields={[
          { key: 'nom', label: 'Nom de l\'action', placeholder: 'Ex: Analyser réponse SMS client' },
          { key: 'agent', label: 'Agent IA', type: 'select', options: [
            { value: 'christelle', label: 'Christelle (Confirmation)' },
            { value: 'yael', label: 'Yaël (Pipeline)' },
            { value: 'rachel', label: 'Rachel (Comptabilité)' },
          ]},
          { key: 'declencheur', label: 'Déclencheur', type: 'select', options: [
            { value: 'reception_sms', label: 'Réception SMS' },
            { value: 'changement_etape', label: 'Changement d\'étape' },
            { value: 'cron', label: 'Tâche planifiée' },
            { value: 'manuel', label: 'Déclenchement manuel' },
          ]},
          { key: 'prompt_template', label: 'Prompt / Instructions IA', wide: true, type: 'textarea', placeholder: 'Analyse le SMS suivant du client {nom} et détermine s\'il confirme ou annule son RDV...' },
          { key: 'action_sortie', label: 'Action en sortie', type: 'select', options: [
            { value: 'change_etape', label: 'Changer d\'étape' },
            { value: 'notify', label: 'Notifier l\'équipe' },
            { value: 'send_sms', label: 'Répondre par SMS' },
            { value: 'log', label: 'Enregistrer seulement' },
          ]},
        ]}
      />

      {/* RÈGLES D'ESCALADE */}
      <AutomationSection
        type="regles_escalade"
        icon={AlertTriangle}
        title="Règles d'Escalade"
        subtitle="Actions automatiques si seuil atteint"
        color="from-red-600/10 to-orange-600/10"
        borderColor="border-red-500/30"
        defaultItem={{ nom: '', condition: '', seuil: 3, periode_jours: 7, action: '', actif: true }}
        fields={[
          { key: 'nom', label: 'Nom', placeholder: 'Ex: 3 NRP → Lead perdu' },
          { key: 'condition', label: 'Condition', wide: true, type: 'textarea', placeholder: 'Ex: 3 NRP consécutifs en 7 jours sans réponse du client' },
          { key: 'seuil', label: 'Seuil (nombre)', type: 'number' },
          { key: 'periode_jours', label: 'Période (jours)', type: 'number' },
          { key: 'action', label: 'Action', type: 'select', options: [
            { value: 'change_etape', label: 'Changer d\'étape automatiquement' },
            { value: 'notify_manager', label: 'Alerter le manager' },
            { value: 'notify_telepro', label: 'Alerter le télépro' },
            { value: 'block', label: 'Bloquer les actions' },
          ]},
        ]}
      />

      {/* NOTIFICATIONS INTERNES */}
      <AutomationSection
        type="notifications_internes"
        icon={Bell}
        title="Notifications Internes"
        subtitle="Alertes pour l'équipe (app, SMS, email)"
        color="from-cyan-600/10 to-blue-600/10"
        borderColor="border-cyan-500/30"
        defaultItem={{ nom: '', destinataire: '', canal: 'app', template: '', declencheur: '', actif: true }}
        fields={[
          { key: 'nom', label: 'Nom', placeholder: 'Ex: Alerte manager si escalade' },
          { key: 'destinataire', label: 'Destinataire', type: 'select', options: [
            { value: 'telepro', label: 'Télépro assigné' },
            { value: 'manager', label: 'Manager' },
            { value: 'technicien', label: 'Technicien' },
            { value: 'all', label: 'Toute l\'équipe' },
          ]},
          { key: 'canal', label: 'Canal', type: 'select', options: [
            { value: 'app', label: 'Dans l\'application' },
            { value: 'sms', label: 'SMS' },
            { value: 'email', label: 'Email' },
            { value: 'whatsapp', label: 'WhatsApp' },
          ]},
          { key: 'template', label: 'Message', wide: true, type: 'textarea', placeholder: 'Le client {nom} n\'a pas confirmé son RDV malgré 2 relances. Action requise.' },
          { key: 'declencheur', label: 'Lié à (événement ou cron)', placeholder: 'Ex: confirm_j1_no_response' },
        ]}
      />
    </div>
  );
};

export default AutomationPanel;
